import { Pagination } from '@shared/components/data-table/datatable.model';

export const TEXT_BUTTON = {
    OK: 'Ok',
    CANCEL: 'Cancelar',
};

export const PAGINATOR_OPTS = {
    TOTAL_COUNT: 0,
    PAGE_INDEX: 0,
    PAGE_SIZE: 10,
    PAGE_SIZE_OPT: [5, 10, 25],
};

// actions
export enum ACTIONS {
    CREATE = 'create',
    UPDATE = 'update',
    PREVIEW = 'preview',
    FETCH = 'fetch',
    CLONE = 'clone',
    ACTIVATE = 'activate',
    DEACTIVATE = 'deactivate',
    DELETE = 'delete',
    MANAGEMENT = 'management',
    MANAGEMENT2 = 'management2',
    SHOW = 'show',
    HISTORY = 'history',
    TOGGLE = 'toggle',
    SHUTDOWN = 'shutdown',
    SHOWTRANSACTION = 'showTransaction',
    RETRY = 'retry',
    CHARGE = 'charge',
    DOWNLOAD = 'download',
    PAY = 'pay',
    UPLOAD = 'upload',
}

// ============================================================
// MATRIZ OBLIGATORIA: Estado de pago (backend key) -> Acción UI
// Cada estado tiene UNA acción exclusiva (spec de diseño)
// ============================================================
export type PaymentStatusKey =
    | 'pending'
    | 'in_process'
    | 'in_validation'
    | 'invalid_card'
    | 'no_card_data'
    | 'invalid_card_data'
    | 'rejected'
    | 'overdue'
    | 'cancelled'
    | 'cancelled_charge'
    | 'early_checkout'
    | 'applied'
    | 'error';

/** Variante semántica del botón de acción (design system) */
export type PaymentActionVariant = 'primary' | 'neutral' | 'ghost';

export interface PaymentStatusAction {
    /** Texto del botón (o 'Sin acción') */
    label: string;
    /** true => botón; false => texto plano 'Sin acción' */
    isButton: boolean;
    /** Variante visual: primary (azul solido, mueve dinero), neutral (slate suave, corrige datos), ghost (solo lectura) */
    variant: PaymentActionVariant;
    /** Acción a despachar en onAction() */
    type: ACTIONS | null;
}

export const PAYMENT_STATUS_ACTION_MAP: Record<PaymentStatusKey, PaymentStatusAction> = {
    // Spec de diseño: Ver detalle (solo lectura) / Cobrar ahora (mueve dinero) / Actualizar tarjeta (corrige datos)
    pending: { label: 'Ver detalle', isButton: true, variant: 'ghost', type: ACTIONS.PREVIEW },
    in_process: { label: 'Ver detalle', isButton: true, variant: 'ghost', type: ACTIONS.PREVIEW },
    in_validation: { label: 'Ver detalle', isButton: true, variant: 'ghost', type: ACTIONS.PREVIEW },
    invalid_card: { label: 'Ver detalle', isButton: true, variant: 'ghost', type: ACTIONS.PREVIEW },
    no_card_data: { label: 'Actualizar tarjeta', isButton: true, variant: 'neutral', type: ACTIONS.MANAGEMENT },
    invalid_card_data: { label: 'Actualizar tarjeta', isButton: true, variant: 'neutral', type: ACTIONS.MANAGEMENT },
    rejected: { label: 'Ver detalle', isButton: true, variant: 'ghost', type: ACTIONS.PREVIEW },
    overdue: { label: 'Cobrar ahora', isButton: true, variant: 'primary', type: ACTIONS.CHARGE },
    cancelled: { label: 'Ver detalle', isButton: true, variant: 'ghost', type: ACTIONS.PREVIEW },
    cancelled_charge: { label: 'Ver detalle', isButton: true, variant: 'ghost', type: ACTIONS.PREVIEW },
    early_checkout: { label: 'Ver detalle', isButton: true, variant: 'ghost', type: ACTIONS.PREVIEW },
    applied: { label: 'Ver detalle', isButton: true, variant: 'ghost', type: ACTIONS.PREVIEW },
    error: { label: 'Cobrar ahora', isButton: true, variant: 'primary', type: ACTIONS.CHARGE },
};

/** Diccionario estricto estado -> acción (matriz obligatoria de diseño) */
export function getActionByStatus(statusKey: PaymentStatusKey): PaymentStatusAction {
    return PAYMENT_STATUS_ACTION_MAP[statusKey] ?? PAYMENT_STATUS_ACTION_MAP.pending;
}

// mat icon
export enum MAT_ICON {
    ACTIONS = 'more_horiz',
    PREVIEW = 'visibility',
    PREVIEW_OFF = 'visibility_off',
    UPDATE = 'create',
    CLONE = 'file_copy',
    ACTIVATE = 'offline_pin',
    DEACTIVATE = 'restore_from_trash',
    DELETE = 'delete',
    WARN = 'warning',
    CLOSE = 'close',
    ERROR = 'error_outline',
    USER = 'account_circle',
    PASSWORD = 'lock',
    CHECK = 'check_circle_outline',
    MANAGEMENT = 'build',
    DOWNLOAD = 'download',
    DEACTIVATE_TEXT = 'Inactivar',
    ACTIVATE_TEXT = 'Activar',
    KEY = 'key',
    PERCENTAGE = 'percent',
    CALCULATE = 'calculate',
    CREDIT_CARD = 'credit_card',
    TAG = 'tag',
    DESCRIPTION = 'description',
    TRANSACTION = 'paid',
    RETRY_PAY = 'currency_exchange',
    PAYMENT = 'payments',
    UPLOAD = 'upload_file',
    ARROWPATH = 'autorenew',
    TRENDINGUP = 'trending_up',
}

export enum TYPE_MESSAGE {
    SUCCESS = 'success',
    WARN = 'warn',
    ERROR = 'error',
    UNDEFINED = '',
}

export const HERO_ICONS = 'heroicons_solid:';
export const OUTLINE_ICONS = 'heroicons_outline:';

export const MAT_SVG_ICON = {
    /* SOLID ICONS */
    ADD: `${HERO_ICONS}plus-circle`,
    PLUS_ADD: `${HERO_ICONS}plus`,
    DELETE: `${HERO_ICONS}trash`,
    MAIL: `${HERO_ICONS}envelope`,
    PHONE: `${HERO_ICONS}phone`,
    EYE: `${HERO_ICONS}eye`,
    EYE_OFF: `${HERO_ICONS}eye-slash`,
    ERROR: `${HERO_ICONS}exclamation-circle`,
    USER: `${HERO_ICONS}user`,
    X_CIRCLE: `${HERO_ICONS}x-circle`,
    CLOSE: `${HERO_ICONS}x-mark`,
    ID: `${HERO_ICONS}identification`,
    SERVER: `${HERO_ICONS}server`,
    PLUS: `${HERO_ICONS}plus`,
    ROOM: `${HERO_ICONS}office-building`,
    BED: `${HERO_ICONS}home`,
    CALENDAR: `${HERO_ICONS}calendar`,
    DESCRIPTION: `${HERO_ICONS}document-text`,
    SEARCH: `${HERO_ICONS}search`,
    DATE: `${HERO_ICONS}cake`,
    TRASH: `${HERO_ICONS}trash`,
    CAMERA: `${HERO_ICONS}camera`,
    MORE: `${HERO_ICONS}ellipsis-vertical`,
    TRENDING: `${HERO_ICONS}arrow-trending-down`,
    EXPORT: `${HERO_ICONS}arrow-up-circle`,
    REFUND: `${HERO_ICONS}arrow-uturn-left`,
    PAY: `${HERO_ICONS}currency-dollar`,
    KEY: `${HERO_ICONS}key`,
    CALCULATE: `${HERO_ICONS}calculator`,
    LINK: `${HERO_ICONS}link`,
    CARD: `${HERO_ICONS}credit-card`,
    TAG: `${HERO_ICONS}tag`,
    PORTAL: `${HERO_ICONS}globe-americas`,
    COMPANY: `${HERO_ICONS}briefcase`,
    OFFICE: `${HERO_ICONS}building-office`,
    LOCATION: `${HERO_ICONS}map-pin`,
    BILLING: `${HERO_ICONS}banknotes`,
    PERCENTAGE: `${HERO_ICONS}receipt-percent`,
    CALENDAR_DAY: `${HERO_ICONS}calendar-days`,
    ARROW_UP: `${HERO_ICONS}bars-arrow-up`,
    ARROW_DOWN: `${HERO_ICONS}bars-arrow-down`,
    LEFT: `${HERO_ICONS}chevron-left`,
    RIGHT: `${HERO_ICONS}chevron-right`,
    SETTINGS: `${HERO_ICONS}cog-6-tooth`,

    /* Modo de cobro (segundo icono del chip de estado de pago) */
    MODE_AUTO: 'mat_outline:smart_toy',
    MODE_MANUAL: 'mat_outline:person',

    /* OUTLINE ICONS */
    EYE_OUTLINE: `${OUTLINE_ICONS}eye`,
    QR_OUTLINE: `${OUTLINE_ICONS}qr-code`,
    EDIT_OUTLINE: `${OUTLINE_ICONS}pencil`,
    EXPORT_OUTLINE: `${OUTLINE_ICONS}arrow-up-circle`,
    ADD_OUTLINE: `${OUTLINE_ICONS}plus-circle`,
    LINK_OUTLINE: `${OUTLINE_ICONS}link`,
    BACK_OUTLINE: `${OUTLINE_ICONS}chevron-left`,
    NEXT_OUTLINE: `${OUTLINE_ICONS}chevron-right`,
    CASH_OUTLINE: `${OUTLINE_ICONS}banknotes`,
    WALLET_OUTLINE: `${OUTLINE_ICONS}wallet`,
    GIFT_OUTLINE: `${OUTLINE_ICONS}gift`,
    UPLOAD_OUTLINE: `${OUTLINE_ICONS}cloud-arrow-up`,
    PHONE_OUTLINE: `${OUTLINE_ICONS}phone`,
    MAIL_OUTLINE: `${OUTLINE_ICONS}envelope`,
    KEY_OUTLINE: `${OUTLINE_ICONS}key`,
    DESCRIPTION_OUTLINE: `${OUTLINE_ICONS}document-text`,
    TAG_OUTLINE: `${OUTLINE_ICONS}tag`,
    ERROR_OUTLINE: `${OUTLINE_ICONS}exclamation-circle`,
    CALENDAR_OUTLINE: `${OUTLINE_ICONS}calendar`,
    X_CIRCLE_OUTLINE: `${OUTLINE_ICONS}x-circle`,
    X_OUTLINE: `${OUTLINE_ICONS}x-mark`,
    CLOCK_OUTLINE: `${OUTLINE_ICONS}clock`,
    REFRESH_OUTLINE: `${OUTLINE_ICONS}arrow-path`,
    SEARCH_OUTLINE: `${OUTLINE_ICONS}magnifying-glass`,
    WARNING: `${OUTLINE_ICONS}exclamation-triangle`,
    USER_CIRCLE: `${OUTLINE_ICONS}user-circle`,
    CHECK_CIRCLE: `${OUTLINE_ICONS}check-circle`,
    CHECK_OUTLINE: `${OUTLINE_ICONS}check`,
    SIGN_OUT: `${OUTLINE_ICONS}arrow-right-circle`,
    SECURITY: `${OUTLINE_ICONS}lock-closed`,
    ORIGIN: `${OUTLINE_ICONS}rectangle-group`,
    TREND: `${OUTLINE_ICONS}arrow-trending-up`,
    VIRTUAL_TERMINAL: `${OUTLINE_ICONS}cursor-arrow-rays`,
    ALTERNATIVE: `${OUTLINE_ICONS}currency-dollar`,
    RESERVATION: `${OUTLINE_ICONS}bookmark-square`,
    CURRENCY: `${OUTLINE_ICONS}currency-dollar`,
    CLIPBOARD: `${OUTLINE_ICONS}clipboard-document-list`,
    USER_GROUP: `${OUTLINE_ICONS}user-group`,
    COMPANY_OUTLINE: `${OUTLINE_ICONS}briefcase`,
    BUILDING_OUTLINE: `${OUTLINE_ICONS}building-library`,
    OFFICE_OUTLINE: `${OUTLINE_ICONS}building-office`,
    PORTAL_OUTLINE: `${OUTLINE_ICONS}globe-americas`,
    USER_OUTLINE: `${OUTLINE_ICONS}user`,
    ROLES_OUTLINE: `${OUTLINE_ICONS}arrow-left-on-rectangle`,
    SHIELD: `${OUTLINE_ICONS}shield-check`,
    QUESTION: `${OUTLINE_ICONS}question-mark-circle`,
    COPY: `${OUTLINE_ICONS}document-duplicate`,
    SCALE: `${OUTLINE_ICONS}scale`,
    CARD_OUTLINE: `${OUTLINE_ICONS}credit-card`,
    BILLING_OUTLINE: `${OUTLINE_ICONS}banknotes`,
    INFO: `${OUTLINE_ICONS}information-circle`,
    RECHARGE_OUTLINE: `${OUTLINE_ICONS}arrow-trending-up`,
    GIFT: `${OUTLINE_ICONS}gift`,
    USER_PLUS: `${OUTLINE_ICONS}user-plus`,
    BELL: `${OUTLINE_ICONS}bell`,
    REGISTER: `${OUTLINE_ICONS}pencil-square`,
    ORDER: `${OUTLINE_ICONS}bars-4`,
};

// toggle name
export enum STATUS_TOGGLE {
    ENABLE = 'Activo',
    DISABLE = 'Inactivo',
    ENABLED = 'Activado',
    DISABLED = 'Desactivado',
}

// actions
export enum ACTIONS_BUTTON {
    UPDATE = 'update',
    PREVIEW = 'preview',
    ACTIVATE = 'activate',
    DEACTIVATE = 'deactivate',
    DELETE = 'delete',
}

export const OPTIONS_OBJECT = {
    [ACTIONS_BUTTON.UPDATE]: {
        name: 'update',
        label: 'Editar',
        icon: MAT_ICON.UPDATE,
        action: ACTIONS.UPDATE,
        rol: ['ADMIN_ROLE'],
    },
    [ACTIONS_BUTTON.PREVIEW]: {
        name: 'preview',
        label: 'Detalles',
        icon: MAT_ICON.PREVIEW,
        action: ACTIONS.PREVIEW,
        rol: ['ADMIN_ROLE'],
    },
    [ACTIONS_BUTTON.ACTIVATE]: {
        name: 'activate',
        label: 'Activar',
        icon: MAT_ICON.ACTIVATE,
        action: ACTIONS.ACTIVATE,
    },
    [ACTIONS_BUTTON.DEACTIVATE]: {
        name: 'deactivate',
        label: 'Desactivar',
        icon: MAT_ICON.DEACTIVATE,
        action: ACTIONS.DEACTIVATE,
    },
    [ACTIONS_BUTTON.DELETE]: {
        name: 'delete',
        label: 'Eliminar',
        icon: MAT_ICON.DELETE,
        action: ACTIONS.DELETE,
    },
};

export const HTTP_PARAMS_SCROLL = {
    offset: '0',
    limit: '20',
};
export const HTTP_PARAMS_ALL = {
    offset: '0',
    limit: '100',
};
export const HTTP_PARAMS_FULL = {
    offset: '0',
    limit: '1000',
};
export const HTTP_PARAMS_SMALL = {
    offset: '0',
    limit: '5',
};

export enum FILTERS_LABEL {
    FILTERS = 'filters',
    SORTERS = 'sorters',
    LIMIT = 'limit',
    OFFSET = 'offset',
    IS_ARCHIVED = 'is_archived',
    EXTERNAL = 'external',
    FIREWALL = 'firewall',
}

export enum LOADING_STATE {
    LOADING = 'LOADING',
    NO_DATA = 'no-data',
    ERROR = 'error',
    FINISHED = 'FINISH',
}

export const genderType = [
    {
        id: 0,
        name: 'Hombre',
    },
    {
        id: 1,
        name: 'Mujer',
    },
    {
        id: 2,
        name: 'Personalizado',
    },
];
export const pronType = [
    {
        id: 0,
        name: 'Masculino',
    },
    {
        id: 1,
        name: 'Femenino',
    },
    {
        id: 2,
        name: 'Neutro',
    },
];

export type CRITERIAS_TYPE = {
    name: string;
    key: string;
    tip: string;
    management?: boolean;
    icon?: string;
};
export const CRITERIAS_POLICY: CRITERIAS_TYPE[] = [
    {
        name: 'Escenario',
        key: 'scenarios',
        tip: 'Indica escenario en donde se aplica la política, ej: cobro standar, no-show o cancelación.',
        management: false,
        icon: MAT_SVG_ICON.COMPANY,
    },
    {
        name: 'Noches a cobrar',
        key: 'charge_nights',
        tip: 'Indica el número de noches a cobrar.',
        management: true,
        icon: MAT_SVG_ICON.CURRENCY,
    },
    {
        name: 'Momento',
        key: 'timeframes',
        tip: 'Horas a tener en cuenta a partir de la fecha indicada para realizar el cobro, ej: 24hrs o 48hrs.',
        management: true,
        icon: MAT_SVG_ICON.CALENDAR,
    },
    {
        name: 'M. tiempo',
        key: 'timings',
        tip: 'Marco de tiempo: determina el tiempo para aplicar cobro ej: antes, después o igual a una fecha determinada.',
        management: false,
        icon: MAT_SVG_ICON.VIRTUAL_TERMINAL,
    },
    {
        name: 'F. referencia',
        key: 'ref_dates',
        tip: 'fecha de referencia: indica la fecha a utilizar para el cobro.',
        management: false,
        icon: MAT_SVG_ICON.CARD,
    },
    {
        name: 'Cuotas',
        key: 'installments',
        tip: 'Indica el número de cuotas a cobrar',
        management: true,
        icon: MAT_SVG_ICON.CALCULATE,
    },
];

export enum SPINNER_STATE {
    SHOW = 'LOADING',
    HIDE = 'FINISHED',
    DONE = 'DONE',
    CHECKED = 'CHECKED',
    FAILED = 'FAILED',
    NOTHING = 'NODATA',
}

export const hotels = [
    {
        id: '13645',
        name: 'Hotel Azuan Suites',
    },
    {
        id: '13644',
        name: 'Hotel Avexi Suites',
    },
    {
        id: '13643',
        name: 'Hotel Marina Suites',
    },
    {
        id: '14364',
        name: 'Bocagrande Cartagena',
    },
    {
        id: '13633',
        name: 'Hotel Aixo Suites',
    },
    {
        id: '17491',
        name: 'Hotel Rodadero Inn',
    },
    {
        id: '16255',
        name: 'Madisson Inn Hotel & Luxury Suites',
    },
    {
        id: '15736',
        name: 'Hotel 1525 By Gh Suites',
    },
    {
        id: '17644',
        name: 'Hotel Abi Inn',
    },
    {
        id: '18004',
        name: 'MT Hotel Windsor House Inn By GEH Suites',
    },
];

export const STATUS_LIST = [
    {
        id: '0',
        name: 'Pendiente',
        class: 'bg-blue-200/30',
        text: 'text-blue-700',
    },
    {
        id: '1',
        name: 'Checkin',
        class: 'bg-green-200/30',
        text: 'text-green-700',
    },
    {
        id: '2',
        name: 'No-Show',
        class: 'bg-gray-100/30',
        text: 'text-gray-700',
    },
    {
        id: '3',
        name: 'Cancelado',
        class: 'bg-red-200/30',
        text: 'text-red-700',
    },
    {
        id: '4',
        name: 'Salida anticipada',
        class: 'bg-gray-300/30',
        text: 'text-gray-700',
    },
    {
        id: '6',
        name: 'Cobro automático cancelado',
        class: 'bg-pink-500/30',
        text: 'text-pink-700',
    },
];
export const STATUS_LIST_OBJ = {
    '0': {
        name: 'Pendiente',
        code: '0',
        class: 'bg-blue-200/30',
        text: 'text-blue-700',
    },
    '1': {
        name: 'Checkin',
        code: '1',
        class: 'bg-green-200/30',
        text: 'text-green-700',
    },
    '2': {
        name: 'No-Show',
        code: '2',
        class: 'bg-gray-100/30',
        text: 'text-gray-400',
    },
    '3': {
        name: 'Cancelado',
        code: '3',
        class: 'bg-red-200/30',
        text: 'text-red-700',
    },
    '4': {
        name: 'Salida anticipada',
        code: '4',
        class: 'bg-gray-300/30',
        text: 'text-gray-500',
    },
    '5': {
        name: 'Reembolso',
        code: '5',
        class: 'bg-yellow-500/30',
        text: 'text-yellow-700',
    },
    '6': {
        name: 'Cobro automático cancelado',
        code: '6',
        class: 'bg-pink-500/30',
        text: 'text-pink-700',
    },
};

export const STATUS_BOOKING = [
    {
        id: 'Aprobado',
        name: 'Aprobado',
        title: 'Aplicados',
        code: 'applied',
        border: 'border-green-300',
        text: 'text-green-700',
    },
    {
        id: 'Sin procesar',
        name: 'Sin procesar',
        title: 'Sin procesar',
        code: 'pending',
        border: 'border-orange-300',
        text: 'text-orange-700',
    },
    {
        id: 'Declinado',
        name: 'Declinado',
        title: 'Rechazados',
        code: 'rejected',
        border: 'border-red-300',
        text: 'text-red-700',
    },
    {
        id: 'Pendiente de aprobación',
        name: 'Pendiente de aprobación',
        title: 'Programados',
        code: 'programmed',
        border: 'border-primary-300',
        text: 'text-primary-700',
    },
];

export const STATUS_PAY = [
    {
        id: 'Aplicado',
        name: 'Aplicado',
        class: 'bg-green-500/30',
        text: 'text-green-700',
    },
    {
        id: 'Aprobado',
        name: 'Aprobado',
        class: 'bg-green-500/30',
        text: 'text-green-700',
    },
    {
        id: 'Cancelado',
        name: 'Cancelado',
        class: 'bg-red-500/30',
        text: 'text-red-700',
    },
    {
        id: 'Declinado',
        name: 'Declinado',
        class: 'bg-red-500/30',
        text: 'text-red-700',
    },
    {
        id: 'Salida anticipada',
        name: 'Salida anticipada',
        class: 'bg-orange-500/30',
        text: 'text-orange-700',
    },
    {
        id: 'Pendiente de aprobación',
        name: 'Pendiente de aprobación',
        class: 'bg-purple-500/30',
        text: 'text-purple-700',
    },
    {
        id: 'Sin procesar',
        name: 'Sin procesar',
        class: 'bg-orange-500/30',
        text: 'text-orange-700',
    },
    {
        id: 'Pago en proceso',
        name: 'Pago en proceso',
        class: 'bg-yellow-500/30',
        text: 'text-yellow-700',
    },
    {
        id: 'Tarjeta no válida',
        name: 'Tarjeta no válida',
        class: 'bg-pink-500/30',
        text: 'text-pink-700',
    },
    {
        id: 'Rechazado',
        name: 'Rechazado',
        class: 'bg-rose-500/30',
        text: 'text-rose-700',
    },
    {
        id: 'Reembolso parcial',
        name: 'Reembolso parcial',
        class: 'bg-lime-500/30',
        text: 'text-lime-700',
    },
    {
        id: 'Reembolso total',
        name: 'Reembolso total',
        class: 'bg-emerald-500/30',
        text: 'text-emerald-700',
    },
    {
        id: 'Reembolso en proceso',
        name: 'Reembolso en proceso',
        class: 'bg-amber-500/30',
        text: 'text-amber-700',
    },
    {
        id: 'Reembolso rechazado',
        name: 'Reembolso rechazado',
        class: 'bg-red-500/30',
        text: 'text-red-700',
    },
];

export const STATUS_PAY_OBJ = {
    Aplicado: {
        name: 'Pago exitoso',
        code: '1',
        class: 'bg-[#ECFDF5] font-[600] text-[.8125rem]  rounded-[9999px] border border-[#A7F3D0] mt-1 mb-1',
        text: 'text-[#047857]',
        icon: null,
        svg: MAT_SVG_ICON.CHECK_CIRCLE,
    },
    'Pago aplicado': {
        name: 'Pago exitoso',
        code: '2',
        class: 'bg-[#ECFDF5] font-[600] text-[.8125rem] rounded-[9999px] border border-[#A7F3D0] mt-1 mb-1',
        text: 'text-[#047857]',
        icon: null,
        svg: MAT_SVG_ICON.CHECK_CIRCLE,
    },
    Cancelado: {
        name: 'Pago cancelado',
        code: '3',
        class: 'bg-[#F3F4F6] font-[600] text-[.8125rem] rounded-[9999px] border border-[#E5E7EB] mt-1 mb-1',
        text: 'text-[#4B5563]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'Pago Cancelado': {
        name: 'Pago cancelado',
        code: '4',
        class: 'bg-[#F3F4F6] font-[600] text-[.8125rem] rounded-[9999px] border border-[#E5E7EB] mt-1 mb-1',
        text: 'text-[#4B5563]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'Salida anticipada': {
        name: 'Salida anticipada',
        code: '5',
        class: 'bg-[#F3F4F6] font-[600] text-[.8125rem] rounded-[9999px] border border-[#E5E7EB] mt-1 mb-1',
        text: 'text-[#4B5563]',
        icon: null,
        svg: MAT_SVG_ICON.SIGN_OUT,
    },
    Pendiente: {
        name: 'Pago pendiente',
        code: '6',
        class: 'bg-[#EFF6FF] font-[600] text-[.8125rem] rounded-[9999px] border border-[#BFDBFE] mt-1 mb-1',
        text: 'text[#1D4ED8]',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    'Pendiente de aprobación': {
        name: 'Pago pendiente de aprobación',
        code: '7',
        class: 'bg-purple-200/30 font-[600] text-[.8125rem] rounded-[9999px] border border-purple-700 mt-1 mb-1',
        text: 'text-purple-700',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    'Sin procesar': {
        name: 'Pago pendiente',
        code: '8',
        class: 'bg-orange-200/30 font-[600] text-[.8125rem] rounded-[9999px] border border-orange-700 mt-1 mb-1',
        text: 'text-orange-700',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    'Pendiente de pago': {
        name: 'Pago pendiente',
        code: '9',
        class: 'bg-[#EA580C] font-[600] text-[.8125rem] rounded-[9999px] border border-[#EA580C] mt-1 mb-1',
        text: 'text-[#FFFFFF]',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    'Pago en proceso': {
        name: 'Procesando pago',
        code: '10',
        class: 'bg-[#F0F9FF] font-[600] text-[.8125rem] rounded-[9999px] border border-[#BAE6FD] mt-1 mb-1',
        text: 'text-[#0369A1]',
        icon: null,
        svg: MAT_SVG_ICON.WARNING,
    },
    Rechazado: {
        name: 'Pago rechazado',
        code: '11',
        class: 'bg-[#FEF2F2] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FECACA] mt-1 mb-1',
        text: 'text-[#B91C1C]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'Pago rechazado': {
        name: 'Pago rechazado',
        code: '12',
        class: 'bg-[#FEF2F2] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FECACA] mt-1 mb-1',
        text: 'text-[#B91C1C]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'Tarjeta no válida': {
        name: 'Datos inválidos de tarjeta',
        code: '13',
        class: 'bg-[#FEF2F2] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FECACA] mt-1 mb-1',
        text: 'text-[#B91C1C]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'Reembolso parcial': {
        name: 'Reembolso parcial',
        code: '14',
        class: 'bg-lime-200/30 font-[600] text-[.8125rem] rounded-[9999px] border border-blue-600 mt-1 mb-1',
        text: 'text-lime-700',
        icon: null,
        svg: MAT_SVG_ICON.WARNING,
    },
    'Reembolso total': {
        name: 'Reembolso total',
        code: '15',
        class: 'bg-[#ECFDF5] font-[600] text-[.8125rem] rounded-[9999px] border border-[#A7F3D0] mt-1 mb-1',
        text: 'text-[#047857]',
        icon: null,
        svg: MAT_SVG_ICON.WARNING,
    },
    'Reembolso en proceso': {
        name: 'Reembolso en proceso',
        code: '16',
        class: 'bg-purple-200/30 font-[600] text-[.8125rem] rounded-[9999px] border border-purple-700 mt-1 mb-1',
        text: 'text-purple-700',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    'Reembolso rechazado': {
        name: 'Reembolso rechazado',
        code: '17',
        class: 'bg-[#FEF2F2] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FECACA] mt-1 mb-1',
        text: 'text-[#B91C1C]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'Link de pago': {
        name: 'Link de pago',
        code: '18',
        class: 'bg-teal-600 font-[600] text-[.8125rem] rounded-[9999px] border border-teal-600 mt-1 mb-1',
        text: 'text-white',
        icon: 'link',
        svg: null,
    },
    Automatizada: {
        name: 'Automatizada',
        code: '19',
        class: 'bg-blue-600 font-[600] text-[.8125rem] rounded-[9999px] border border-blue-600 mt-1 mb-1',
        text: 'text-white',
        icon: 'autorenew',
        svg: null,
    },
    'Datáfono virtual': {
        name: 'Terminal virtual',
        code: '20',
        class: 'bg-violet-600 font-[600] text-[.8125rem] rounded-[9999px] border border-violet-600 mt-1 mb-1',
        text: 'text-white',
        icon: 'credit_card',
        svg: null,
    },
    Programación: {
        name: 'Pago futuro pendiente',
        code: '21',
        class: 'bg-[#F0F9FF] font-[600] text-[.8125rem] rounded-[9999px] border border-[#BAE6FD] mt-1 mb-1',
        text: 'text-[#0369A1]',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    Aprobado: {
        name: 'Pago exitoso',
        code: '22',
        class: 'bg-[#ECFDF5] font-[600] text-[.8125rem] rounded-[9999px] border border-[#A7F3D0] mt-1 mb-1',
        text: 'text-[#047857]',
        icon: null,
        svg: MAT_SVG_ICON.CHECK_CIRCLE,
    },
    Declinado: {
        name: 'Pago rechazado',
        code: '23',
        class: 'bg-[#FEF2F2] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FECACA] mt-1 mb-1',
        text: 'text-[#B91C1C]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'En proceso': {
        name: 'En proceso',
        code: '10',
        class: 'bg-[#F0F9FF] font-[600] text-[.8125rem] rounded-[9999px] border border-[#BAE6FD] mt-1 mb-1',
        text: 'text-[#0369A1]',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    'Recaudo: Aprobado': {
        name: 'Pago exitoso',
        code: '1',
        class: 'bg-[#ECFDF5] font-[600] text-[.8125rem]  rounded-[9999px] border border-[#A7F3D0] mt-1 mb-1',
        text: 'text-[#047857]',
        icon: null,
        svg: MAT_SVG_ICON.CHECK_CIRCLE,
    },
    'Recaudo: Cancelado': {
        name: 'Pago cancelado',
        code: '4',
        class: 'bg-[#FEF2F2] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FECACA] mt-1 mb-1',
        text: 'text-[#B91C1C]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'Recaudo: Aprobación pendiente': {
        name: 'Pago pendiente de aprobación',
        code: '6',
        class: 'bg-purple-200/30 font-[600] text-[.8125rem] rounded-[9999px] border border-purple-700 mt-1 mb-1',
        text: 'text-purple-700',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    'Recaudo: Rechazado': {
        name: 'Pago rechazado',
        code: '11',
        class: 'bg-[#FEF2F2] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FECACA] mt-1 mb-1',
        text: 'text-[#B91C1C]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'Reembolso: Aprobado': {
        name: 'Reembolso: Aprobado',
        code: '15',
        class: 'bg-[#ECFDF5] font-[600] text-[.8125rem] rounded-[9999px] border border-[#A7F3D0] mt-1 mb-1',
        text: 'text-[#047857]',
        icon: null,
        svg: MAT_SVG_ICON.WARNING,
    },
    'Reembolso: Aprobación pendiente': {
        name: 'Reembolso: Aprobación pendiente',
        code: '16',
        class: 'bg-purple-200/30 font-[600] text-[.8125rem] rounded-[9999px] border border-purple-700 mt-1 mb-1',
        text: 'text-purple-700',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    'Reembolso: Rechazado': {
        name: 'Reembolso: Rechazado',
        code: '17',
        class: 'bg-[#FEF2F2] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FECACA] mt-1 mb-1',
        text: 'text-[#B91C1C]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
};
export const STATUS_PAY_OBJ_DATAPHONE = {
    Aplicado: {
        name: 'Cobro exitoso',
        code: '1',
        class: 'bg-[#ECFDF5] font-[600] text-[.8125rem]  rounded-[9999px] border border-[#A7F3D0] mt-1 mb-1',
        text: 'text-[#047857]',
        icon: null,
        svg: MAT_SVG_ICON.CHECK_CIRCLE,
    },
    'Pago aplicado': {
        name: 'Cobro exitoso',
        code: '2',
        class: 'bg-[#ECFDF5] font-[600] text-[.8125rem] rounded-[9999px] border border-[#A7F3D0] mt-1 mb-1',
        text: 'text-[#047857]',
        icon: null,
        svg: MAT_SVG_ICON.CHECK_CIRCLE,
    },
    Cancelado: {
        name: 'Cobro cancelado',
        code: '3',
        class: 'bg-[#F3F4F6] font-[600] text-[.8125rem] rounded-[9999px] border border-[#E5E7EB] mt-1 mb-1',
        text: 'text-[#4B5563]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'Pago Cancelado': {
        name: 'Cobro cancelado',
        code: '4',
        class: 'bg-[#F3F4F6] font-[600] text-[.8125rem] rounded-[9999px] border border-[#E5E7EB] mt-1 mb-1',
        text: 'text-[#4B5563]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'Salida anticipada': {
        name: 'Salida anticipada',
        code: '5',
        class: 'bg-[#F3F4F6] font-[600] text-[.8125rem] rounded-[9999px] border border-[#E5E7EB] mt-1 mb-1',
        text: 'text-[#4B5563]',
        icon: null,
        svg: MAT_SVG_ICON.SIGN_OUT,
    },
    Pendiente: {
        name: 'Cobro pendiente',
        code: '6',
        class: 'bg-[#EFF6FF] font-[600] text-[.8125rem] rounded-[9999px] border border-[#BFDBFE] mt-1 mb-1',
        text: 'text[#1D4ED8]',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    'Pendiente de aprobación': {
        name: 'Cobro Pendiente de aprobación',
        code: '7',
        class: 'bg-purple-200/30 font-[600] text-[.8125rem] rounded-[9999px] border border-purple-700 mt-1 mb-1',
        text: 'text-purple-700',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    'Sin procesar': {
        name: 'Cobro pendiente',
        code: '8',
        class: 'bg-[#FFE4D6] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FF8A65] mt-1 mb-1',
        text: 'text-[#BF360C]',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    'Pendiente de pago': {
        name: 'Cobro pendiente',
        code: '9',
        class: 'bg-[#EA580C] font-[600] text-[.8125rem] rounded-[9999px] border border-[#EA580C] mt-1 mb-1',
        text: 'text-[#FFFFFF]',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    'Pago en proceso': {
        name: 'Procesando cobro',
        code: '10',
        class: 'bg-[#F0F9FF] font-[600] text-[.8125rem] rounded-[9999px] border border-[#BAE6FD] mt-1 mb-1',
        text: 'text-[#0369A1]',
        icon: null,
        svg: MAT_SVG_ICON.WARNING,
    },
    Rechazado: {
        name: 'Tarjeta rechazada',
        code: '11',
        class: 'bg-[#FEF2F2] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FECACA] mt-1 mb-1',
        text: 'text-[#B91C1C]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'Pago rechazado': {
        name: 'Tarjeta rechazada',
        code: '12',
        class: 'bg-[#FEF2F2] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FECACA] mt-1 mb-1',
        text: 'text-[#B91C1C]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'Tarjeta no válida': {
        name: 'Datos inválidos de tarjeta',
        code: '13',
        class: 'bg-[#FEF2F2] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FECACA] mt-1 mb-1',
        text: 'text-[#B91C1C]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'Reembolso parcial': {
        name: 'Reembolso parcial',
        code: '14',
        class: 'bg-lime-200/30 font-[600] text-[.8125rem] rounded-[9999px] border border-blue-600 mt-1 mb-1',
        text: 'text-lime-700',
        icon: null,
        svg: MAT_SVG_ICON.WARNING,
    },
    'Reembolso total': {
        name: 'Reembolso total',
        code: '15',
        class: 'bg-[#ECFDF5] font-[600] text-[.8125rem] rounded-[9999px] border border-[#A7F3D0] mt-1 mb-1',
        text: 'text-[#047857]',
        icon: null,
        svg: MAT_SVG_ICON.WARNING,
    },
    'Reembolso en proceso': {
        name: 'Reembolso en proceso',
        code: '16',
        class: 'bg-purple-200/30 font-[600] text-[.8125rem] rounded-[9999px] border border-purple-700 mt-1 mb-1',
        text: 'text-purple-700',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    'Reembolso rechazado': {
        name: 'Reembolso rechazado',
        code: '17',
        class: 'bg-[#FEF2F2] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FECACA] mt-1 mb-1',
        text: 'text-[#B91C1C]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'Link de pago': {
        name: 'Link de pago',
        code: '18',
        class: 'bg-slate-100 font-[600] text-[.8125rem] rounded-[9999px] border border-slate-600 mt-1 mb-1',
        text: 'text-slate-600',
        icon: 'link',
        svg: null,
    },
    Automatizada: {
        name: 'Automatizada',
        code: '19',
        class: 'bg-blue-100 font-[600] text-[.8125rem] rounded-[9999px] border border-blue-600 mt-1 mb-1',
        text: 'text-blue-600',
        icon: 'autorenew',
        svg: null,
    },
    'Datáfono virtual': {
        name: 'Terminal virtual',
        code: '20',
        class: 'bg-violet-100 font-[600] text-[.8125rem] rounded-[9999px] border border-violet-600 mt-1 mb-1',
        text: 'text-violet-600',
        icon: 'credit_card',
        svg: null,
    },
    Programación: {
        name: 'Cobro futuro pendiente',
        code: '21',
        class: 'bg-[#F0F9FF] font-[600] text-[.8125rem] rounded-[9999px] border border-[#BAE6FD] mt-1 mb-1',
        text: 'text-[#0369A1]',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    Aprobado: {
        name: 'Cobro exitoso',
        code: '22',
        class: 'bg-[#ECFDF5] font-[600] text-[.8125rem] rounded-[9999px] border border-[#A7F3D0] mt-1 mb-1',
        text: 'text-[#047857]',
        icon: null,
        svg: MAT_SVG_ICON.CHECK_CIRCLE,
    },
    Declinado: {
        name: 'Tarjeta rechazada',
        code: '23',
        class: 'bg-[#FEF2F2] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FECACA] mt-1 mb-1',
        text: 'text-[#B91C1C]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'En proceso': {
        name: 'En proceso',
        code: '10',
        class: 'bg-[#F0F9FF] font-[600] text-[.8125rem] rounded-[9999px] border border-[#BAE6FD] mt-1 mb-1',
        text: 'text-[#0369A1]',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    'Recaudo: Aprobado': {
        name: 'Cobro exitoso',
        code: '1',
        class: 'bg-[#ECFDF5] font-[600] text-[.8125rem]  rounded-[9999px] border border-[#A7F3D0] mt-1 mb-1',
        text: 'text-[#047857]',
        icon: null,
        svg: MAT_SVG_ICON.CHECK_CIRCLE,
    },
    'Recaudo: Cancelado': {
        name: 'Cobro cancelado',
        code: '4',
        class: 'bg-[#FEF2F2] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FECACA] mt-1 mb-1',
        text: 'text-[#B91C1C]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'Recaudo: Aprobación pendiente': {
        name: 'Cobro Pendiente de aprobación',
        code: '6',
        class: 'bg-purple-200/30 font-[600] text-[.8125rem] rounded-[9999px] border border-purple-700 mt-1 mb-1',
        text: 'text-purple-700',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    'Recaudo: Rechazado': {
        name: 'Tarjeta rechazada',
        code: '11',
        class: 'bg-[#FEF2F2] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FECACA] mt-1 mb-1',
        text: 'text-[#B91C1C]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
    'Reembolso: Aprobado': {
        name: 'Reembolso: Aprobado',
        code: '15',
        class: 'bg-[#ECFDF5] font-[600] text-[.8125rem] rounded-[9999px] border border-[#A7F3D0] mt-1 mb-1',
        text: 'text-[#047857]',
        icon: null,
        svg: MAT_SVG_ICON.WARNING,
    },
    'Reembolso: Aprobación pendiente': {
        name: 'Reembolso: Aprobación pendiente',
        code: '16',
        class: 'bg-purple-200/30 font-[600] text-[.8125rem] rounded-[9999px] border border-purple-700 mt-1 mb-1',
        text: 'text-purple-700',
        icon: null,
        svg: MAT_SVG_ICON.CLOCK_OUTLINE,
    },
    'Reembolso: Rechazado': {
        name: 'Reembolso: Rechazado',
        code: '17',
        class: 'bg-[#FEF2F2] font-[600] text-[.8125rem] rounded-[9999px] border border-[#FECACA] mt-1 mb-1',
        text: 'text-[#B91C1C]',
        icon: null,
        svg: MAT_SVG_ICON.X_OUTLINE,
    },
};

export const MODEL_PAY = [
    {
        id: 'Link de pago',
        name: 'Link de pago',
    },
    {
        id: 'Automatizada',
        name: 'Automatizada',
    },
    {
        id: 'Reembolso',
        name: 'Reembolso',
    },
    {
        id: 'Datáfono virtual',
        name: 'Datáfono virtual',
    },
];
export const PORTAL_LIST = [
    { id: 'Expedia', name: 'Expedia' },
    { id: 'Despegar', name: 'Despegar' },
    { id: 'Booking.com', name: 'Booking' },
    { id: 'Hiperguest', name: 'Hyperguest' },
    { id: 'Hotelbed', name: 'Hotelbed' },
    { id: 'RoomCloud', name: 'Roomcloud' },
    { id: 'Hopper', name: 'Hopper' },
    { id: 'Prices Travel', name: 'Prices Travel' },
    { id: 'Escalabed', name: 'Escalabed' },
    { id: 'TBO', name: 'TBO' },
    { id: 'Roibos', name: 'Roibos' },
    { id: 'siteminder_engine', name: 'Siteminder Engine' },
    { id: 'cloudbeds_engine', name: 'Cloudbeds Engine' },
    { id: 'simple_booking', name: 'Simple Booking' },
    { id: 'airbnb', name: 'Airbnb' },
    { id: 'hostelworld', name: 'Hostelworld' },
    { id: 'mirai', name: 'Mirai' },
    { id: 'gnahs', name: 'GNA Hotel Solutions' },
];

export const PORTAL_CODE = {
    Expedia: {
        src: 'assets/images/portal/expedia.svg',
        code: 'Expedia',
        label: 'Expedia',
    },
    expedia: {
        src: 'assets/images/portal/expedia.svg',
        code: 'expedia',
        label: 'Expedia',
    },
    Despegar: {
        src: 'assets/images/portal/despegar.svg',
        code: 'Despegar',
        label: 'Despegar',
    },
    'Booking.com': {
        src: 'assets/images/portal/booking.svg',
        code: 'Booking.com',
        label: 'Booking.com',
    },
    Hiperguest: {
        src: 'assets/images/portal/HyperGuest.svg',
        code: 'Hiperguest',
        label: 'Hyperguest',
    },
    hyperguest: {
        src: 'assets/images/portal/HyperGuest.svg',
        code: 'hyperguest',
        label: 'Hyperguest',
    },
    Hotelbed: {
        src: 'assets/images/portal/hotelbed.svg',
        code: 'Hotelbed',
        label: 'Hotelbed',
    },
    RoomCloud: {
        src: 'assets/images/portal/roomcloud.png',
        code: 'RoomCloud',
        label: 'Roomcloud',
    },
    Hopper: {
        src: 'assets/images/portal/hopper.svg',
        code: 'Hopper',
        label: 'Hopper',
    },
    'Prices Travel': {
        src: 'assets/images/portal/princeTravel.svg',
        code: 'Prices Travel',
        label: 'Prices Travel',
    },
    Escalabed: {
        src: 'assets/images/portal/Escalabed.png',
        code: 'Escalabed',
        label: 'Escalabed',
    },
    TBO: {
        src: 'assets/images/portal/TBO.png',
        code: 'TBO',
        label: 'TBO',
    },
    Roibos: {
        src: 'assets/images/portal/roibos.png',
        code: 'Roibos',
        label: 'Roibos',
    },
    Roiback: {
        src: 'assets/images/portal/roiback.png',
        code: 'Roiback',
        label: 'Roiback',
    },
    'Siteminder Engine': {
        src: 'assets/images/portal/siteminder.png',
        code: 'siteminder_engine',
        label: 'Siteminder Engine',
    },
    'Cloudbeds Engine': {
        src: 'assets/images/portal/cloudbeds.svg',
        code: 'cloudbeds_engine',
        label: 'Cloudbeds Engine',
    },
    ProfitRoom: {
        src: 'assets/images/portal/profitroom.png',
        code: 'profitroom',
        label: 'ProfitRoom',
    },
    'Open Travel Service': {
        src: 'assets/images/portal/openTravel.jpg',
        code: 'open_travel_service',
        label: 'Open Travel Service',
    },
    WebBeds: {
        src: 'assets/images/portal/webbeds.png',
        code: 'webbeds',
        label: 'WebBeds',
    },
    Airbnb: {
        src: 'assets/images/portal/airbnb.png',
        code: 'airbnb',
        label: 'Airbnb',
    },
    'Simple Booking': {
        src: 'assets/images/portal/simple_booking.png',
        code: 'simple_booking',
        label: 'Simple Booking',
    },
    Hostelworld: {
        src: 'assets/images/portal/Hostelworld.png',
        code: 'hostelworld',
        label: 'Hostelworld',
    },
    Mirai: {
        src: 'assets/images/portal/Mirai.png',
        code: 'mirai',
        label: 'Mirai',
    },
    'GNA Hotel Solutions': {
        src: 'assets/images/portal/GNAHS.png',
        code: 'gnahs',
        label: 'GNA Hotel Solutions',
    },
};

export const TYPE_KEY = {
    Api_Key: 'Api',
    Session_Token: 'Token',
    Asymmetric_Encryption: 'Cifrado asimétrico',
    Pgp_Encryption: 'Cifrado PGP',
};

export enum ROLES {
    Holding = 'holding',
    Agency = 'agency',
    Hotel = 'hotel',
    HyperAdmin = 'admin',
    Demo = 'demo',
}

export const EXP_MONTH = [
    { id: 13, name: 'No aplica' },
    { id: 1, name: '1' },
    { id: 2, name: '2' },
    { id: 3, name: '3' },
    { id: 4, name: '4' },
    { id: 5, name: '5' },
    { id: 6, name: '6' },
    { id: 7, name: '7' },
    { id: 8, name: '8' },
    { id: 9, name: '9' },
    { id: 10, name: '10' },
    { id: 11, name: '11' },
    { id: 12, name: '12' },
];

export const EXP_YEAR = [
    { id: 13, name: 'No aplica' },
    { id: 2025, name: '2025' },
    { id: 2026, name: '2026' },
    { id: 2027, name: '2027' },
    { id: 2028, name: '2028' },
    { id: 2029, name: '2029' },
    { id: 2030, name: '2030' },
    { id: 2031, name: '2031' },
    { id: 2032, name: '2032' },
    { id: 2033, name: '2033' },
    { id: 2034, name: '2034' },
    { id: 2035, name: '2035' },
    { id: 2036, name: '2036' },
    { id: 2037, name: '2037' },
    { id: 2038, name: '2038' },
    { id: 2039, name: '2039' },
    { id: 2040, name: '2040' },
    { id: 2041, name: '2041' },
    { id: 2042, name: '2042' },
    { id: 2043, name: '2043' },
    { id: 2044, name: '2044' },
];

export const HOURS = [
    { id: 0, name: '00:00' },
    { id: 1, name: '01:00' },
    { id: 2, name: '02:00' },
    { id: 3, name: '03:00' },
    { id: 4, name: '04:00' },
    { id: 5, name: '05:00' },
    { id: 6, name: '06:00' },
    { id: 7, name: '07:00' },
    { id: 8, name: '08:00' },
    { id: 9, name: '09:00' },
    { id: 10, name: '10:00' },
    { id: 11, name: '11:00' },
    { id: 12, name: '12:00' },
    { id: 13, name: '13:00' },
    { id: 14, name: '14:00' },
    { id: 15, name: '15:00' },
    { id: 16, name: '16:00' },
    { id: 17, name: '17:00' },
    { id: 18, name: '18:00' },
    { id: 19, name: '19:00' },
    { id: 20, name: '20:00' },
    { id: 21, name: '21:00' },
    { id: 22, name: '22:00' },
    { id: 23, name: '23:00' },
];

/**
 * @name FILE_MAX_SIZE
 * @description Max file size allowed. 2 MB
 */
export const FILE_MAX_SIZE = 70000000;

/**
 * @name FILE_MAX_SIZE_LABEL
 * @description Max file size allowed. 2 MB
 */
export const FILE_MAX_SIZE_LABEL = '70 MB';

export const encrypt_frontend =
    'LS0tLS1CRUdJTiBQVUJMSUMgS0VZLS0tLS0KTUlJQklqQU5CZ2txaGtpRzl3MEJBUUVGQUFPQ0FROEFNSUlCQ2dLQ0FRRUFyZ05PVno4akZBSk1acDNMWWEwago0RHNVbXdZa0lKNFVNNnQvbjNJaXZWOVNhYUVJUTE5ZkR0YytYQU9weDBIU2xrek02UHkxc1ZrNjYrb2FFWEJHCmk4eUVMOUQ2aWFNSHRaN3hPbEdiUER1dm1UWnZqb0dyeTVyS0hIWjdEckFwWXVyZEx1dHltdkZWeDVCYVRrNGgKdndodTBHZXlMYm1KL2NwbVBSejgrdGVUSzd5aWRvazA1Zm5HT0VMaWJLdVJ6R3lkSDhGOE40cVpnN2EyK1Z3cQp1TWcxM2l0Z3dMMm9rUFFtWWNTOHFac0tWcFJPSDRNTHRMRjdPWGpOZ3lrbitiYzBNdFBHcDhYWkcwL21pRjJLCmN0WEtMRlFoYk5zdjBad1BkSjdhNitWYVg4TzlmQU53aFVxdmpXdXFES2F1LzFJTGJESVNRcHhmMjM3SmtCc0EKZFFJREFRQUIKLS0tLS1FTkQgUFVCTElDIEtFWS0tLS0tCg==';
export const desencrypt_backend =
    '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';
export const codesError: any = {
    'Unrecognized credit card company':
        'Compañía de tarjetas de crédito no reconocida',
    incomplete_cvc: 'CVC incompleto',
    incomplete_date: 'Fecha incompleta',
    date_in_past: 'Fecha en pasadp',
    invalid_expiration_year: 'Año de expiración invalida',
    incomplete_card_number: 'Número de tarjeta incompleto',
    incorrect_card_number: 'Número de tarjeta incorrecto',
};

export const actionsList = {
    edit: { name: 'Editar', icon: MAT_SVG_ICON.EDIT_OUTLINE },
    detail: { name: 'Detalles', icon: MAT_SVG_ICON.EYE_OUTLINE },
    refund: { name: 'Reembolso', icon: 'autorenew' },
    voucher: { name: 'Recibo', icon: 'download' },
    add_voucher: { name: 'Adjuntar comprobante', icon: 'upload_file' },
    transations: { name: 'Transaciones', icon: 'trending_up' },
    report: { name: 'Reporte transacción', icon: 'assignment_returned' },
    delete: { name: 'Eliminar', icon: 'delete' },
    manage_status: { name: 'Gestionar estado', icon: 'adjust' },
    asociate_hotel: { name: 'Hoteles asociados', icon: 'business' },
    manage_policy: { name: 'Gestionar política', icon: 'assignment' },
    manage_user: { name: 'Gestionar usuarios', icon: 'assignment_ind' },
    manage_permission: { name: 'Gestionar permisos', icon: 'build' },
    keys: { name: 'Claves de acceso', icon: 'key' },
    breakdown: { name: 'Desglose', icon: 'view_headline' },
    file: { name: 'Archivar', icon: 'file_copy' },
    link: { name: 'Generar link', icon: 'link' },
    cancel: { name: 'Cancelar cobro', icon: 'cancel_presentation' },
    change_status: { name: 'Cambiar estado', icon: 'low_priority' },
    edit_card: {
        name: 'Editar datos de tarjeta',
        icon: MAT_SVG_ICON.EDIT_OUTLINE,
    },
    register_pms: {
        name: 'Registrar pago en PMS',
        icon: MAT_SVG_ICON.REGISTER,
    },
    logs: {
        name: 'Logs del registro',
        icon: 'assignment'
    },
    notified: {
        name: 'Notificación webhook',
        icon: 'alarm_on'
    }
};

export const routeMap: { [key: string]: string } = {
    main: 'Inicio',
    link: 'QR & Links de pago',
    list: 'Lista',
    bookings: 'Cobros automáticos',
    management: 'Creación',
    details: 'Detalles',
    detail: 'Detalles',
    dataphone: 'Terminal virtual',
    pay: 'Crear Pago',
    'payment-alternative': 'Pagos alternativos',
    transactions: 'Transaciones',
    'hotel/list': 'Lista',
    billing: 'Facturación',
    'bookings-room': 'Reservas',
    payments: 'Pagos',
    summary: 'Resumen',
    'hyper/list': 'Lista',
    'holdings-management': 'Grupo empresarial',
    create: 'Crear',
    holdingDemo: 'Grupo empresarial Demo',
    manegement: 'Gestionar',
    'company-management': 'Razon social',
    edit: 'Editar',
    'agency-management': 'Agencias',
    'hotel-management': 'Comercios',
    'policy-management': 'Gestión de Politicas',
    register: 'Registro',
    demo: 'Demo',
    status: 'Estado',
    'criterias-management': 'Criterios de políticas',
    'plans-management': 'Planes de Reservas',
    'otas-management': 'Otas',
    'parameterization-management': 'Parametrización',
    'documents/list': 'Lista',
    'documents/create': 'Crear Documento',
    'trm-management': 'TRM',
    'user-management': 'Usuarios',
    'apikey-management': 'Clave de Acceso',
    permissions: 'Permisos',
    'roles-management': 'Roles',
    dashboard: 'Estadisticas',
    balance: 'Balance',
    overview: 'Lista',
    transactionsHyper: 'Transaciones',
    'edit-card': 'Editar datos de tarjeta',
    'sub-policy-management': 'Gestión de sub Políticas',
    'pms-management': 'Gestión de PMS',
    'channel-management': 'Gestión de Channel',

    // Agrega aquí el mapeo de todas las rutas de tu aplicación
};

export const methodsAux = {
    spanish: [
        {
            name: 'Con Tarjeta de crédito o débito',
            description: 'Ingresas los datos y paga online.',
            code: 'credit-card',
            code2: 'credit-card',
            codeHotel: 'credit_card',
            subItems: false,
            expanded: false,
            icon: MAT_SVG_ICON.CARD_OUTLINE,
        },
        {
            name: 'Desde tu Banco',
            description: 'Paga online desde tu cuenta bancaria.',
            code: 'pse',
            code2: 'pse',
            codeHotel: 'bank_transfer',
            subItems: false,
            expanded: false,
            icon: MAT_SVG_ICON.BUILDING_OUTLINE,
        },
        {
            name: 'En Efectivo',
            description: 'Creas referencia y vas a un punto de pago.',
            code: 'cash',
            code2: 'cash',
            codeHotel: 'cash',
            subItems: false,
            expanded: false,
            icon: MAT_SVG_ICON.CASH_OUTLINE,
        },
        {
            name: 'Con Billetera digital',
            description: 'Accedes a tu billetera y pagas online.',
            code: 'wallet',
            code2: 'wallet',
            codeHotel: 'wallet',
            subItems: false,
            expanded: false,
            icon: MAT_SVG_ICON.WALLET_OUTLINE,
        },
        {
            name: 'A Crédito',
            description: 'Compra ahora y pagas después. Pide tu Crédito',
            code: 'credit',
            code2: 'credit',
            codeHotel: 'credit',
            subItems: true,
            expanded: false,
            icon: MAT_SVG_ICON.BILLING_OUTLINE,
        },
        {
            name: 'Tarjeta regalo',
            description: 'Realiza pago con saldo cargado previamente.',
            code: 'gift-card',
            code2: 'gift-card',
            codeHotel: 'gift_cards',
            subItems: false,
            expanded: false,
            icon: MAT_SVG_ICON.GIFT_OUTLINE,
        },
        {
            name: 'Saldo prepagado',
            description: 'Realiza pago con saldo cargado previamente.',
            code: 'preloaded-balance',
            code2: 'preloaded-balance',
            codeHotel: 'preloaded_balance',
            subItems: true,
            expanded: false,
            icon: MAT_SVG_ICON.BILLING_OUTLINE,
        },
        
    ],
    english: [
        {
            name: 'With Credit or Debit Card',
            description: 'Enter your card details and pay online.',
            code: 'credit-card',
            code2: 'credit-card',
            codeHotel: 'credit_card',
            subItems: false,
            expanded: false,
            icon: MAT_SVG_ICON.CARD_OUTLINE,
        },
        {
            name: 'From Your Bank',
            description: 'Pay online from your bank account.',
            code: 'pse',
            code2: 'pse',
            codeHotel: 'bank_transfer',
            subItems: false,
            expanded: false,
            icon: MAT_SVG_ICON.BUILDING_OUTLINE,
        },
        {
            name: 'In Cash',
            description: 'Create a reference and go to a payment point.',
            code: 'cash',
            code2: 'cash',
            codeHotel: 'cash',
            subItems: false,
            expanded: false,
            icon: MAT_SVG_ICON.CASH_OUTLINE,
        },
        {
            name: 'With Digital Wallet',
            description: 'Access your wallet and pay online.',

            code: 'wallet',
            code2: 'wallet',
            codeHotel: 'wallet',
            subItems: false,
            expanded: false,
            icon: MAT_SVG_ICON.WALLET_OUTLINE,
        },
        {
            name: 'Credit',
            description: 'Buy now, pay later. Apply for your loan.',
            code: 'credit',
            code2: 'credit',
            codeHotel: 'credit',
            subItems: true,
            expanded: false,
            icon: MAT_SVG_ICON.BILLING_OUTLINE,
        },
        {
            name: 'Gift Card',
            description: 'Make a payment with previously loaded balance.',
            code: 'gift-card',
            code2: 'gift-card',
            codeHotel: 'gift_cards',
            subItems: false,
            expanded: false,
            icon: MAT_SVG_ICON.GIFT_OUTLINE,
        },
        {
            name: 'Prepaid Balance',
            description: 'Make a payment with previously loaded balance.',
            code: 'preloaded-balance',
            code2: 'preloaded-balance',
            codeHotel: 'preloaded_balance',
            subItems: true,
            expanded: false,
            icon: MAT_SVG_ICON.BILLING_OUTLINE,
        },
         
    ],
};
export const steppersTitle = {
    spanish: {
        info: 'Información',
        client: 'Cliente',
        promotional: 'Código promocional',
        buy: 'Compra',
        pay: 'Pago',
        recharge: 'Recarga',
        confirm: 'Confimación',
        developed: 'Desarrollado por',
        security: 'Seguridad garantizada',
        buyOnli: 'Compra online en',
        payCount: 'Valor a pagar',
        concept: 'Concepto de servicio a pagar',
        titleOne: 'Información del pago',
        titleTwo: 'Información del cliente',
        titleThree: '¿Cómo prefieres pagar?',
        titleFour: 'Cargando datos del pago',
        titleFive: 'Tarjeta de crédito o débito',
        titleSix: 'Pago con Transferencia bancaria',
        titleSeven: 'Tarjeta de regalo',
        titleEight: 'Aplica código promocional',
        titleNine: '¿Tienes un codigo promocional?',
        next: 'Siguiente',
        goBack: 'Volver',
        buttonPay: 'Pagar',
        buttonCancel: 'Cancelas',
        reference: 'Su referencia de pago es',
        agreement: 'Convenio',
        code: 'Código',
        ticket: 'Ticket de pago',
        idClient: 'ID cliente',
        total: 'Total',
        order: 'Tu orden expirará',
        instruction: 'Instrucciones de pago',
        referenceDownload: 'Descargar referencia de pago',
        summary: 'Resumen de transacción',
        status: 'Estado',
        holder: 'Titular',
        authorizationCode: 'Código de autorización',
        transactionDate: 'Fecha de transacción',
        retry: 'Reintentar Pago',
        download: 'Descargar comprobante',
        return: 'Regresar al comercio',
        validate: 'Validar',
        skip: 'Omitir',
        redeem: 'Redimir',
        getInto: 'Ingresar',
        buyer: 'Comprador',
        customer: 'Usuario (huésped)',
        personalData:'Datos personales',
        contactDetails: 'Datos de contacto',
        isUser:'¿La persona que compra es la misma que recibe el servicio?',
        isBuy:'¿La persona Titular del pago es la misma persona que compra?',
        yes:'Si',
        no:'No',
        cardDetails:'Datos de tarjeta',
        paymentDetails:'Datos de pago',
        ownerDetails:'Datos del titular',
        bankDetails: 'Datos bancarios',
        clic: '(Click aquí para continuar)',
        consult:'Consultar'
    },
    english: {
        info: 'Information',
        client: 'Customer ',
        promotional: 'Promotional code',
        buy: 'Buy',
        pay: 'Pay',
        recharge: 'Recharge',
        confirm: 'Confimation',
        developed: 'Developed by',
        security: 'Guaranteed security',
        buyOnli: 'Buy online at',
        payCount: 'Amount to pay',
        concept: 'Concept of service to be paid',
        titleOne: 'Payment information',
        titleTwo: 'Customer information',
        titleThree: 'How do you prefer to pay?',
        titleFour: 'Loading payment data',
        titleFive: 'Credit or debit card',
        titleSix: 'Payment by bank transfer',
        titleSeven: 'Gift card',
        titleEight: 'Apply promotional code',
        titleNine: 'Do you have a promotional code?',
        next: 'Next',
        goBack: 'go Back',
        buttonPay: 'Pay',
        buttonCancel: 'Cancel',
        reference: 'Your payment reference is',
        agreement: 'Agreement',
        code: 'Code',
        ticket: 'Payment ticket',
        idClient: 'Client ID',
        total: 'Total',
        order: 'Your order will expire',
        instruction: 'Payment instructions',
        referenceDownload: 'Download payment reference',
        summary: 'Transaction summary',
        status: 'Status',
        holder: 'Holder',
        authorizationCode: 'Authorization code',
        transactionDate: 'Transaction date',
        retry: 'Retry Payment',
        download: 'Download receipt',
        return: 'Return to trade',
        validate: 'Validate',
        skip: 'Skip',
        redeem: 'Redeem',
        getInto: 'Get into',
        buyer: 'Buyer',
        customer: 'User (guest)',
        personalData:'Personal data',
        contactDetails: 'Contact details',
        isUser:'Is the person who buys the same person who receives the service?',
        isBuy:'Is the person making the payment the same person who is buying?',
        yes:'Yes',
        no:'No',
        cardDetails:'Card details',
        paymentDetails:'Payment details',
        ownerDetails:'Owner details',
        bankDetails: 'Bank details',
        clic: '(Click here to continue)',
        consult:'Consult'
    },
};

export const formTitles = {
    spanish: {
        currency: {
            title: 'Moneda de transacción (Requerido)',
            description: 'Seleccione una moneda.',
        },
        amount: {
            title: 'Monto total a pagar (Requerido)',
            description: 'Debe incluir IVA y Propina, si aplica.',
            extra: 'Usa puntos para miles y coma para decimales. Ejemplo: 1.234,56',
        },
        concept: {
            title: 'Concepto de servicio a pagar',
            description: '',
        },
        fullName: { title: 'Nombre completo (Requerido)', description: '' },
        email: { title: 'Correo electrónico (Requerido)', description: '' },
        phone: {
            title: 'Celular (Preferible Whatsapp) (Requerido)',
            description: '',
        },
        address: { title: 'Dirección', description: '' },
        creditNumber: {
            title: 'Número de tarjeta',
            description: 'Ingrese el número de la tarjeta',
        },
        holder: {
            title: 'Titular de la tarjeta',
            description: 'Ingrese el nombre del titular',
            extra: '¿Usar datos del cliente como titular de la tarjeta?',
        },
        installments: { title: 'Cuotas', description: '' },
        name: { title: 'Nombres', description: '' },
        lastName: { title: 'Apellidos', description: '' },
        emailOp: {
            title: 'Correo electrónico',
            description: 'Ingrese el correo',
        },
        phoneOp: { title: 'N° télefono', description: 'Número télefonico' },
        personType: { title: 'Tipo de persona', description: '' },
        documentType: { title: 'Tipo de documento', description: '' },
        documentNumber: { title: 'Número de documento', description: '' },
        expirationDate: {
            title: 'Fecha de expiración',
            description: 'Mes/Año',
            error1: 'Formato inválido. Usa Mes/Año',
            error2: 'El mes debe ser entre 01 y 12',
            error3: 'El año debe ser 25 o mayor',
            error4: 'Campo obligatorio',
            error5: 'Fecha invalida',
            example: 'Ej: 02/30',
        },
        bank: { title: 'Banco', description: '' },
        codeGiftCard: { title: 'Código' },
        codeOTP: { title: 'Código OTP' },
        promotionalCode: { title: 'Código promocional' },
    },
    english: {
        currency: {
            title: 'Transaction currency (Required)',
            description: 'Select a currency.',
        },
        amount: {
            title: 'Total amount to pay (Required)',
            description: 'Must include IVA and Tip, if applicable.',
            extra: 'Use periods for thousands and commas for decimals. Example: 1,234.56',
        },
        concept: {
            title: 'Concept of service to be paid',
            description: '',
        },
        fullName: { title: 'Full name (Required)', description: '' },
        email: { title: 'Email (Required)', description: '' },
        phone: {
            title: 'Cell phone (Whatsapp preferred) (Required)',
            description: '',
        },
        address: { title: 'Address', description: '' },
        creditNumber: {
            title: 'Card number',
            description: 'Enter the card number',
        },
        holder: {
            title: 'Cardholder',
            description: 'Enter the name of the holder',
            extra: 'Use customer data as a cardholder?',
        },
        installments: { title: 'Installments', description: '' },
        name: { title: 'Names', description: '' },
        lastName: { title: 'Last name', description: '' },
        emailOp: { title: 'Email', description: 'Enter the email' },
        phoneOp: { title: 'Phone number', description: 'Phone number' },
        personType: { title: 'Person type', description: '' },
        documentType: { title: 'Document Type', description: '' },
        documentNumber: { title: 'Document Number', description: '' },
        expirationDate: {
            title: 'Expiration date',
            description: 'Month/Year',
            error1: 'Invalid format. Use Month/Year',
            error2: 'The month must be between 01 and 12',
            error3: 'The year must be 25 or older',
            error4: 'Required field',
            error5: 'Invalid date',
            example: 'Ex: 02/30',
        },
        bank: { title: 'bank', description: '' },
        codeGiftCard: { title: 'Gift card code' },
        codeOTP: { title: 'OTP code' },
        promotionalCode: { title: 'Promotional code' },
    },
};

export const PAYMENT_CODES = {
    credit_card: 'Tarjeta de crédito/débito',
    pse: 'PSE',
    boton_bancolombia: 'Botón Bancolombia',
    nequi: 'Nequi',
    daviplata: 'Daviplata',
    sinpe_movil: 'Sinpe Móvil',
    preloaded_balance: 'Saldos prepagados',
    gift_cards: 'Tarjeta regalo',
};

export const CASH_PAYMENT_IMAGES = {
    corresponsal: '/assets/icons/bancolombia.svg',
    daviplata: '/assets/icons/daviplata.svg',
    efecty_nuvei: '/assets/icons/efecty.svg',
    efecty_monnet: '/assets/icons/efecty.svg',
    efecty_redeban: '/assets/icons/efecty.svg',
    puntored: '/assets/icons/puntored.svg',
    nequi: '/assets/icons/nequi.svg',
    qrbancolombia: '/assets/icons/bancolombia.svg',
    pse: '/assets/icons/pse.svg',
    boton_bancolombia: '/assets/icons/boton_bancolombia.png',
    breb: '/assets/icons/bre-b.png',
    pagui:'/assets/icons/pagui.png'
};

export const QUOTAS = [
    { id: 1, name: 1 },
    { id: 2, name: 2 },
    { id: 3, name: 3 },
    { id: 4, name: 4 },
    { id: 5, name: 5 },
    { id: 6, name: 6 },
    { id: 7, name: 7 },
    { id: 8, name: 8 },
    { id: 9, name: 9 },
    { id: 10, name: 10 },
    { id: 11, name: 11 },
    { id: 12, name: 12 },
    { id: 13, name: 13 },
    { id: 14, name: 14 },
    { id: 15, name: 15 },
    { id: 16, name: 16 },
    { id: 17, name: 17 },
    { id: 18, name: 18 },
];

export const PAGINATION: Pagination = {
    limit: '20',
    offset: '0',
};

export const QRHotel = {
    azuan_suites: 'assets/qr/azuan_suites_qr.png',
    ghs_1525: 'assets/qr/ghs_1525_qr.png',
    libertg_hotels_spa: 'assets/qr/libertg_hotels_spa_qr.png',
    abi_inn: 'assets/qr/abi_inn_qr.png',
    aixo_suites: 'assets/qr/aixo_suites_qr.png',
    avexi_suites: 'assets/qr/avexi_suites_qr.png',
    hotel_axis: 'assets/qr/hotel_axis_qr.png',
    bocagrande_ctg: 'assets/qr/bocagrande_ctg_qr.png',
    cannatel: 'assets/qr/cannatel_qr.png',
    casa_belle_luxury_house: 'assets/qr/casa_belle_luxury_house_qr.png',
    casa_botero: 'assets/qr/casa_botero_qr.png',
    hotel_chiniu: 'assets/qr/hotel_chiniu_qr.png',
    elan_at_ballena_beach: 'assets/qr/elan_at_ballena_beach_qr.png',
    hotel_exelsior: 'assets/qr/hotel_exelsior_qr.png',
    hotel_hukumeizi: 'assets/qr/hotel_hukumeizi_qr.png',
    infinity_hotel: 'assets/qr/infinity_hotel_qr.png',
    la_playita_isla_fuerte: 'assets/qr/la_playita_isla_fuerte_qr.png',
    madisson_inn: 'assets/qr/madisson_inn_qr.png',
    mandala_belen: 'assets/qr/mandala_belen_qr.png',
    mandala_botero: 'assets/qr/mandala_botero_qr.png',
    mandala_centro: 'assets/qr/mandala_centro_qr.png',
    marina_suites: 'assets/qr/marina_suites_qr.png',
    panorama: 'assets/qr/panorama_qr.png',
    andes_plaza: 'assets/qr/andes_plaza_qr.png',
    garden_laureles: 'assets/qr/garden_laureles_qr.png',
    playa_club: 'assets/qr/playa_club_qr.png',
    playa_paraiso: 'assets/qr/playa_paraiso_qr.png',
    qosqo_wasi: 'assets/qr/qosqo_wasi_qr.png',
    rodadero_inn: 'assets/qr/rodadero_inn_qr.png',
    hotel_samakwa: 'assets/qr/hotel_samakwa_qr.png',
    hotel_sansiraka: 'assets/qr/hotel_sansiraka_qr.png',
    sm_Grand: 'assets/qr/sm_Grand_qr.png',
    terra_biohotel: 'assets/qr/terra_biohotel_qr.png',
    the_Art_hotel: 'assets/qr/the_Art_hotel_qr.png',
    hotel_del_tropico: 'assets/qr/hotel_del_tropico_qr.png',
    windsor_house: 'assets/qr/windsor_house_qr.png',
    z3_hotel: 'assets/qr/z3_hotel_qr.png',
    hotel_zaraya: 'assets/qr/hotel_zaraya_qr.png',
    zulita: 'assets/qr/zulita_qr.png',
    bioma_boutique_hotel: 'assets/qr/bioma_boutique_hotel_qr.png',
    hotel_boquilla_suites: 'assets/qr/hotel_boquilla_suites_qr.png',
    aborigenes_hotel: 'assets/qr/aborigenes_hotel_qr.png',
    hotel_villas_de_san_sebastian:
        'assets/qr/hotel_villas_de_san_sebastian_qr.png',
    hotel_casa_73_deluxe: 'assets/qr/hotel_casa_73_deluxe_qr.png',
    palermo_home: 'assets/qr/palermo_home_qr.png',
    albarrada_hotel_boutique: 'assets/qr/albarrada_hotel_boutique_qr.png',
    hotel_porton_bogota: 'assets/qr/hotel_porton_bogota_qr.png',
    porton_cabrera_hotel: 'assets/qr/porton_cabrera_hotel_qr.png',
    hotel_rodadero_dorado: 'assets/qr/hotel_rodadero_dorado_qr.png',
    hotel_regatta: 'assets/qr/hotel_regatta_qr.png',
    hotel_villa_los_candiles: 'assets/qr/hotel_villa_los_candiles_qr.png',
    in_di_house_of_reggae: 'assets/qr/in_di_house_of_reggae_qr.png',
    hotel_casa_linda_vista: 'assets/qr/hotel_casa_linda_vista_qr.png',
    hotel_la_casona_laureles: 'assets/qr/hotel_la_casona_laureles_qr.png',
    sueno_pacifico: 'assets/qr/sueno_pacifico_qr.png',
    colours_oasis_resort: 'assets/qr/colours_oasis_resort_qr.png',
    cabanas_la_laguna_lodge: 'assets/qr/cabanas_la_laguna_lodge_qr.png',
    lares_de_la_70: 'assets/qr/lares_de_la_70_qr.png',
    el_cielo_en_la_tierra: 'assets/qr/el_cielo_en_la_tierra_qr.png',
    malik_hotel_boutique: 'assets/qr/malik_hotel_boutique_qr.png',
    patio_andaluz_hotel_boutique:
        'assets/qr/patio_andaluz_hotel_boutique_qr.png',
    nogal_suites: 'assets/qr/nogal_suites_qr.png',
    nogal_suite_pasto: 'assets/qr/nogal_suite_pasto_qr.png',
    nogal_suite_ipiales: 'assets/qr/nogal_suite_ipiales_qr.png',
    internacional_nogal: 'assets/qr/internacional_nogal_qr.png',
    hotel_bali_poblado: 'assets/qr/hotel_bali_poblado_qr.png',
    hotel_kasa_dokari: 'assets/qr/hotel_kasa_dokari_qr.png',
    rancho_ecoturistico_el_faro: 'assets/qr/rancho_ecoturistico_el_faro_qr.png',
    hotel_foresto_365: 'assets/qr/hotel_foresto_365_qr.png',
    hotel_finca_la_fortaleza: 'assets/qr/hotel_finca_la_fortaleza_qr.png',
    living_35: 'assets/qr/living_35_qr.png',
    molinos_plaza: 'assets/qr/molinos_plaza_qr.png',
    mar_botanico: 'assets/qr/mar_botanico_qr.png',
    atardeceres_del_cafe: 'assets/qr/atardeceres_del_cafe_qr.png',
    cartagena_for_travelers: 'assets/qr/cartagena_for_travelers_qr.png',
    white_coral: 'assets/qr/white_coral_qr.png',
    hotel_carigua: 'assets/qr/hotel_carigua_qr.png',
    hotel_palmas_del_estadio: 'assets/qr/hotel_palmas_del_estadio_qr.png',
    premium_real_hotel: 'assets/qr/premium_real_hotel_qr.png',
    hotel_calypso_beach: 'assets/qr/hotel_calypso_beach_qr.png',
    novus_laureles: 'assets/qr/novus_laureles_qr.png',
    hgo_hotel: 'assets/qr/hgo_hotel_qr.png',
    cauca_viejo_fundadores: 'assets/qr/cauca_viejo_fundadores_qr.png',
    dunama_hotel: 'assets/qr/dunama_hotel_qr.png',
    cartagena_plaza: 'assets/qr/cartagena_plaza_qr.png',
    la_quebrada_ecolodge: 'assets/qr/la_quebrada_ecolodge_qr.png',
    veca_flats_centenario: 'assets/qr/veca_flats_centenario_qr.png',
    azure_lofts_and_pool: 'assets/qr/azure_lofts_and_pool_qr.png',
    galy_rod_house: 'assets/qr/galy_rod_house_qr.png',
    balcon_tamesino_hotel: 'assets/qr/balcon_tamesino_hotel_qr.png',
    lureles_de_aburra_hotel: 'assets/qr/lureles_de_aburra_hotel_qr.png',
    'masaya-experience': 'assets/qr/masaya-experience_qr.png',
    'masaya-medellin': 'assets/qr/masaya-medellin_qr.png',
    'masaya-cartagena': 'assets/qr/masaya-cartagena_qr.png',
    'masaya-la-pasión': 'assets/qr/masaya-la-pasion_qr.png',
    'masaya-bogota': 'assets/qr/masaya-bogota_qr.png',
    'masaya-camelia': 'assets/qr/masaya-camelia_qr.png',
    isla_bonita_hotel: 'assets/qr/isla_bonita_hotel_qr.png',
    koray_hotel: 'assets/qr/koray_hotel_qr.png',
    villeta_suite_hotel: 'assets/qr/villeta_suite_hotel_qr.png',
    the_host_hotel: 'assets/qr/the_host_hotel_qr.png',
    slob_hotel: 'assets/qr/slob_hotel_qr.png',
    torre_del_prado_apartahotel: 'assets/qr/torre_del_prado_apartahotel_qr.png',
    plaza_de_toros_apartamento_boutique:
        'assets/qr/plaza_de_toros_apartamento_boutique_qr.png',
    burbi_lake_lodge: 'assets/qr/burbi_lake_lodge_qr.png',
    golden_valley_hotel: 'assets/qr/golden_valley_hotel_qr.png',
    rental_pro_management: 'assets/qr/rental_pro_management_qr.png',
    playa_salguero_hotel: 'assets/qr/playa_salguero_hotel_qr.png',
    casa_avenida_jimenez: 'assets/qr/casa_avenida_jimenez_qr.png',
    palonegro_ecolodge: 'assets/qr/palonegro_ecolodge_qr.png',
    tierra_del_mar_hotel_boutique:
        'assets/qr/tierra_del_mar_hotel_boutique_qr.png',
    the_charlee_hotel: 'assets/qr/the_charlee_hotel_qr.png',
    casa_hendaus_hotel_boutique: 'assets/qr/casa_hendaus_hotel_boutique_qr.png',
    cafe_hotel_medellin: 'assets/qr/cafe_hotel_medellin_qr.png',
    dorado_sai_hotel_campestre: 'assets/qr/dorado_sai_hotel_campestre_qr.png',
    obelisco_hotel_boutique: 'assets/qr/obelisco_hotel_boutique_qr.png',
    loma_verde_hotel: 'assets/qr/loma_verde_hotel_qr.png',   
    ada_marina_chalet:'assets/qr/ada_marina_chalet_qr.png',
    ryo_74b_suites: 'assets/qr/ryo_74b_suites_qr.png'

};

export const currencyCode = {
    USD: {
        id: 'USD',
        name: 'Dólar estadounidense',
        symbol: '$',
    },
    COP: {
        id: 'COP',
        name: 'Peso Colombiano',
        symbol: '$',
    },
    CLP: {
        id: 'CLP',
        name: 'Peso Chileno',
        symbol: '$',
    },
    PEN: {
        id: 'PEN',
        name: 'Sol Peruano',
        symbol: 'S/.',
    },
    CRC: {
        id: 'CRC',
        name: 'Colón Costarricence',
        symbol: '₡',
    },
};
export const personType = {
    spanish: [
        { name: 'Persona natural', id: 'natural' },
        { name: 'Persona jurídica', id: 'legal' },
    ],
    english: [
        { name: 'Natural person', id: 'natural' },
        { name: 'Legal entity', id: 'legal' },
    ],
};
export const documentType = {
    natural: {
        spanish: [
            { name: 'Cédula de ciudadanía', id: 'national_id', code:'CC' },
            { name: 'Pasaporte', id: 'passport', code:'PP' },
            {
                name: 'Documento de identificación extranjero ',
                id: 'residence_permit', code:'CE'
            },
        ],
        english: [
            { name: 'National Id', id: 'national_id',code:'NI' },
            { name: 'Passport', id: 'passport',code:'PP' },
            { name: 'Resident Permit ', id: 'residence_permit',code:'RP' },
        ],
    },
    legal: {
        spanish: [{ name: 'Número de identificación tributario', id: 'tax_id',code:'NIT' }],
        english: [{ name: 'Tax id', id: 'tax_id',code:'TI' }],
    },
};
export const equivalentes = {
    ip: {
        spanish: 'Ip',
        english: 'Ip'
    },
    name: {
        spanish: 'Nombre',
        english: 'Name'
    },
    lastname: {
        spanish: 'Apellidos',
        english: 'Lastname'
    },
    document_type: {
        spanish: 'Tipo de documento',
        english: 'Document type'
    },
    document_number: {
        spanish: 'Número de documento',
        english: 'Document number'
    },
    person_type: {
        spanish: 'Tipo de persona',
        english: 'Person type'
    },
    email: {
        spanish: 'Correo',
        english: 'Email'
    },
    phone: {
        spanish: 'Telefono',
        english: 'Phone'
    },
    country_code: {
        spanish: 'Código del pais',
        english: 'Country code'
    },
    address: {
        spanish: 'Dirección',
        english: 'Address'
    },
    bank: {
        spanish: 'Banco',
        english: 'Bank'
    },
    count: {
        spanish: 'Monto',
        english: 'Amount'
    },
    cc_code: {
        spanish: 'CVC',
        english: 'CVC'
    },
    cc_number: {
        spanish: 'Número de la tarjeta',
        english: 'Card number'
    },
    cc_expire_month: {
        spanish: 'Fecha de vencimiento',
        english: 'Expire date'
    },
    cc_expire_year: {
        spanish: 'Fecha de vencimiento',
        english: 'Expire date'
    },
    cc_holder: {
        spanish: 'Titular',
        english: 'Holder'
    },
    first_name: {
        spanish: 'Nombre',
        english: 'Name'
    },
}